import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import { useHandoffSeed, skipHandoffContact } from '@/lib/handoff';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import InputStep from './intake/InputStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import TextareaStep from './intake/TextareaStep';
import CatalogCheckoutStep from './intake/CatalogCheckoutStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import BasicContactStep from './intake/BasicContactStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import FileUploadStep from './intake/FileUploadStep';
import CompleteStep from './intake/CompleteStep';
import Footer from './intake/Footer';
import BrandLogo from '@/components/BrandLogo';
import { buildTenantUrl } from '@/lib/tenant';
import {
  sermorelinFlow,
  buildDynamicSermorelinFlow,
  isDisqualifyingAnswer
} from '../data/sermorelinData';
import { apiService } from '../services/api';
import { consentAnswerLabel, composeQaQuestionText, describeSubmitFailure, formatMultiSelectAnswer, splitHeightWeightAnswer, computeAgeFromDob, GH_THERAPY_MIN_AGE } from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

const STORAGE_KEY = 'fitpal_sermorelin_progress';

// Resume modal component
const ResumeModal = ({ onResume, onStartFresh }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Welcome back!
        </h3>
        <p className="text-gray-500">
          We found your previous progress. Would you like to continue where you left off?
        </p>
      </div>
      <div className="space-y-3">
        <button
          onClick={onResume}
          className="btn-primary w-full"
          data-testid="resume-progress-btn"
        >
          Continue where I left off
        </button>
        <button
          onClick={onStartFresh}
          className="w-full py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          data-testid="start-fresh-btn"
        >
          Start fresh
        </button>
      </div>
    </div>
  </div>
);

const SermorelinForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient.
  useHandoffSeed(setAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  // §concierge-oto: the created visit (masterId etc.) for the post-payment offer.
  const [visitResult, setVisitResult] = useState(null);
  const [slideDirection, setSlideDirection] = useState('initial');
  const [slideKey, setSlideKey] = useState(0);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Single-select auto-advance gating (§FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE).
  // advancedForSlideKeyRef ensures we schedule at most ONE advance per slideKey,
  // so a user who changes their selection on the same slide cannot stack timers
  // and accidentally skip the next slide. advanceTimerRef holds the pending
  // timer id so it can be cancelled on unmount.
  const advancedForSlideKeyRef = useRef(null);
  const advanceTimerRef = useRef(null);
  useEffect(() => () => {
    if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
  }, []);

  // Load saved progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.currentStep > 0) {
          setSavedProgress(parsed);
          setShowResumeModal(true);
        }
      }
    } catch (e) {
      console.error('Error loading saved progress:', e);
    }
  }, []);

  // Save progress to localStorage whenever state changes
  useEffect(() => {
    if (currentStep > 0 && !isComplete && !isDisqualified) {
      try {
        const progressData = {
          currentStep,
          answers,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
      } catch (e) {
        console.error('Error saving progress:', e);
      }
    }
  }, [currentStep, answers, isComplete, isDisqualified]);

  // Clear saved progress when form is completed
  const clearSavedProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing progress:', e);
    }
  }, []);

  // Resume from saved progress
  const handleResumeProgress = useCallback(() => {
    if (savedProgress) {
      setCurrentStep(savedProgress.currentStep);
      setAnswers(savedProgress.answers || {});
      setShowResumeModal(false);
    }
  }, [savedProgress]);

  // Start fresh
  const handleStartFresh = useCallback(() => {
    clearSavedProgress();
    setShowResumeModal(false);
    setSavedProgress(null);
  }, [clearSavedProgress]);

  // Build dynamic flow based on current answers
  const preview = usePreview();
  const currentFlow = useMemo(() => {
    return preview.wrapFlow(skipHandoffContact(buildDynamicSermorelinFlow(answers)));
  }, [answers, preview]);

  const totalSteps = currentFlow.length;
  const currentQuestion = currentFlow[currentStep];

  // Handle disqualification
  const handleDisqualify = useCallback((reason) => {
    setIsDisqualified(true);
    setDisqualifyReason(reason);
    clearSavedProgress();
    setSlideDirection('forward');
    setSlideKey(prev => prev + 1);
  }, [clearSavedProgress]);

  // Handle answer
  const handleAnswer = useCallback((questionId, answer) => {
    const previousAnswer = answers[questionId];
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    // Check for disqualification
    const disqualResult = isDisqualifyingAnswer(questionId, answer);
    if (disqualResult.disqualified) {
      handleDisqualify(disqualResult.reason);
      return;
    }

    // Auto-advance for single-select questions — single-fire per slideKey so
    // rapid re-taps on the same slide cannot stack timers and skip the next
    // slide. See FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE.
    if (currentQuestion?.type === 'single-select' && answer !== previousAnswer) {
      if (advancedForSlideKeyRef.current === slideKey) return;
      advancedForSlideKeyRef.current = slideKey;
      if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null;
        setCurrentStep(prev => {
          if (prev >= totalSteps - 1) return prev;
          setSlideDirection('forward');
          setSlideKey(k => k + 1);
          return prev + 1;
        });
      }, 200);
    }
  }, [totalSteps, currentQuestion, answers, handleDisqualify, slideKey]);

  const handleNext = useCallback(() => {
    // Check for disqualification before advancing (important for multi-select)
    if (currentQuestion) {
      const currentAnswer = answers[currentQuestion.id];
      const disqualResult = isDisqualifyingAnswer(currentQuestion.id, currentAnswer);
      if (disqualResult.disqualified) {
        handleDisqualify(disqualResult.reason);
        return;
      }
    }
    
    if (currentStep < totalSteps - 1) {
      setSlideDirection('forward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps, currentQuestion, answers, handleDisqualify]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideDirection('backward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleStartOver = useCallback(() => {
    setSlideDirection('backward');
    setSlideKey(prev => prev + 1);
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setIsDisqualified(false);
    setDisqualifyReason('');
    clearSavedProgress();
  }, [clearSavedProgress]);

  // Get animation class based on direction
  const getAnimationClass = () => {
    switch (slideDirection) {
      case 'forward':
        return 'slide-forward';
      case 'backward':
        return 'slide-backward';
      default:
        return 'slide-initial';
    }
  };

  // Render step based on question type
  const renderStep = () => {
    // Show disqualified screen
    if (isDisqualified) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <DisqualifiedStep 
            reason={disqualifyReason} 
            onStartOver={handleStartOver}
          />
        </div>
      );
    }

    if (!currentQuestion) return null;

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (answer) => handleAnswer(currentQuestion.id, answer),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      setLoading
    };

    const stepContent = (() => {
      switch (currentQuestion.type) {
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
          return <MultiSelectStep {...commonProps} />;
        case 'input':
        case 'number':
        case 'date':
        case 'text':
          return <InputStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'height-weight-bmi':
          return (
            <HeightWeightBmiStep 
              {...commonProps} 
              onDisqualify={handleDisqualify}
              isOnTreatment={false}
            />
          );
        case 'consent-dropdown':
          return (
            <ConsentDropdownStep 
              {...commonProps}
              onDisqualify={handleDisqualify}
            />
          );
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'basic-contact':
          // Sermorelin/Tesamorelin are growth-hormone-pathway therapies — 20+
          // only per Beluga policy (Nikka 2026-06-17). See
          // docs/BACKEND_GH_THERAPY_AGE20_PRD.md.
          return <BasicContactStep {...commonProps} minAge={GH_THERAPY_MIN_AGE} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} />;
        case 'checkout':
          return (
            <CatalogCheckoutStep conciergeUpsell
              catalogVisitType="antiAging"
              // Tesamorelin is currently hidden centrally (data/hiddenMedications.js).
              filter={(i) => /sermorelin|tesamorelin/i.test((i.name || '') + (i.medication || ''))}
              onComplete={async (payment) => {
                // Square charged → create the Beluga visit BEFORE advancing
                // to the Complete page. Without this call the patient pays
                // but no beluga_visit row / doctor review happens.
                try {
                  setIsSubmitting(true);
                  setAnswers(prev => ({ ...prev, 'payment': payment }));

                  const formattedAnswers = Object.entries(answers).flatMap(([questionId, answer]) => {
                    const split = splitHeightWeightAnswer(answer);
                    if (split) return split;
                    const slide = currentFlow.find(q => q.id === questionId);
                    const consentLabel = consentAnswerLabel(slide, answer);
                    let formattedAnswer;
                    if (consentLabel !== null) {
                      formattedAnswer = consentLabel;
                    } else if (Array.isArray(answer)) {
                      formattedAnswer = formatMultiSelectAnswer(answer);
                    } else if (typeof answer === 'object' && answer !== null) {
                      if (answer.preview) {
                        formattedAnswer = `File: ${answer.fileName || 'uploaded'}`;
                      } else {
                        formattedAnswer = JSON.stringify(answer);
                      }
                    } else {
                      formattedAnswer = String(answer);
                    }
                    const questionText = composeQaQuestionText(slide) || questionId;
                    const record = { question: questionText, answer: formattedAnswer, slide_id: questionId, rawAnswer: answer };
                    if (slide && typeof slide.consentText === 'string' && slide.consentText.trim()) {
                      record.consentBody = slide.consentText;
                    }
                    return [record];
                  });

                  const contactInfo = answers['basic-contact'] || {};
                  const shipping = answers['shipping-address'] || {};

                  // Age-20 eligibility gate (sermorelin/tesamorelin). Belt-and-
                  // suspenders behind the BasicContactStep minAge check — never
                  // create a visit for an under-20 patient. BE re-validates
                  // server-side (docs/BACKEND_GH_THERAPY_AGE20_PRD.md).
                  const age = computeAgeFromDob(contactInfo.dob);
                  if (age !== null && age < GH_THERAPY_MIN_AGE) {
                    setIsSubmitting(false);
                    handleDisqualify(
                      `Sermorelin and tesamorelin are only available to patients age ${GH_THERAPY_MIN_AGE} and older. We're unable to proceed with this request.`,
                    );
                    return;
                  }

                  const paymentId = payment?.paymentId || payment?.payment_id || localStorage.getItem('fitpal_intake_payment_id') || '';

                  const visitResp = await apiService.submitForm(formattedAnswers, {
                    first_name: contactInfo.name?.split(' ')[0] || '',
                    last_name: contactInfo.name?.split(' ').slice(1).join(' ') || '',
                    email: contactInfo.email || '',
                    phone: contactInfo.phone || '',
                    dob: contactInfo.dob || '',
                    address: shipping.address1 || '',
                    address2: shipping.address2 || '',
                    city: shipping.city || '',
                    state: shipping.state || '',
                    zip: shipping.zipCode || '',
                  }, paymentId, 'sermorelin', 'metabolicSupport',
                  // §catalog: forward the exact catalog selection so the BE
                  // builds patientPreference from these medIds (REPLACE) and the
                  // charge already priced from the same set. Falls back to the
                  // legacy dose-string path when absent.
                  {
                    prescriptionFulfillment: payment?.prescriptionFulfillment,
                    // §dexa-add-on: forward what the patient actually bought.
                    // Without this the add-on is CHARGED (SquarePaymentForm gets
                    // amount=totalCents, which includes it) while the record of
                    // what it was for never leaves the browser.
                    dexaAddOn: payment?.dexaAddOn,
                      conciergeAddOn: payment?.conciergeAddOn,
                    pharmacySelection: payment?.pharmacySelection,
                    ...((Array.isArray(payment?.selectedMedIds) && payment.selectedMedIds.length)
                      ? { selectedMedIds: payment.selectedMedIds }
                      : {}),
                  });
                  setVisitResult(visitResp);

                  // Beluga photo relay: identity-verification is collected
                  // pre-checkout, so we POST it to setVisitFile only after
                  // create-visit-v2 returns the masterId. Fire-and-forget;
                  // backend autosave already persists the photo locally so a
                  // failed relay can be retried server-side.
                  const idPhoto = answers['identity-verification'];
                  if (idPhoto && idPhoto.preview && visitResp?.masterId) {
                    apiService
                      .uploadVisitPhoto({
                        masterId: visitResp.masterId,
                        visitId: visitResp.visitId,
                        slideId: 'identity-verification',
                        photo: idPhoto,
                      })
                      .catch((err) => {
                        console.warn('[uploadVisitPhoto] identity-verification relay failed:', err?.response?.data || err.message);
                      });
                  }

                  // §embed-bridge: tell the dashboard what was created. Waits for any
                  // in-flight photo relay first — the parent closes its modal on this
                  // message, which unmounts the iframe and would abort the upload.
                  emitVisitCompleteWhenPhotosSettle({
                    masterId: visitResp?.masterId,
                    visitId: visitResp?.visitId,
                    scheduleLink: visitResp?.scheduleLink,
                  });

                  handleNext();
                  clearSavedProgress();
                } catch (error) {
                  console.error('Visit creation error:', error);
                  // §embed-bridge: relay the RAW backend error — the dashboard reads
                  // errorCode/refundId to tell the patient their card was refunded.
                  emitVisitError(error);
                  alert(describeSubmitFailure(error));
                } finally {
                  setIsSubmitting(false);
                }
              }}
              isProcessing={isSubmitting}
              answers={answers}
              setLoading={setLoading}
              clearSavedProgress={clearSavedProgress}
              currentStep={currentStep}
              timestamp={Date.now()}
            />
          );
        case 'complete':
          return (
            <CompleteStep
              STORAGE_KEY={STORAGE_KEY}
              answers={answers}
              visitType="metabolicSupport"
              visitResult={visitResult}
              onComplete={() => {}}
            />
          );
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        default:
          return <QuestionStep {...commonProps} />;
      }
    })();

    return (
      <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
        {preview.frame(currentQuestion, stepContent, handleBack)}
      </div>
    );
  };

  return (
    <>
      <div className='intake-form-loader' style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>

      <div className={`min-h-screen flex flex-col ${preview.rootClass}`} data-testid="sermorelin-form" style={{ display: loading === false ? '' : 'none' }}>
        
        {/* Resume Modal */}
        {showResumeModal && (
          <ResumeModal 
            onResume={handleResumeProgress}
            onStartFresh={handleStartFresh}
          />
        )}

        {/* Header */}
        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {/* Back button */}
            {currentStep > 0 && !isComplete && !isDisqualified ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="back-button"
                style={{display: currentQuestion?.type === 'complete' ? 'none' : ''}}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <a
                href={buildTenantUrl('/')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="home-link"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Home</span>
              </a>
            )}

            {/* Logo */}
            <div className="text-center flex-1">
              <BrandLogo
                alt="FitPal"
                className="mx-auto"
                style={{ width: '400px', height: 'auto', maxWidth: '100%' }}
              />
              <p className="text-xs text-gray-500 mt-1">Metabolic Support Program</p>
            </div>

            <div className="w-16 flex-shrink-0" />
          </div>
        </header>

        {/* Progress Bar */}
        {!isComplete && !isDisqualified && totalSteps > 0 && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">
              {preview.progress(currentQuestion, currentStep, totalSteps)}
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div className={`w-full ${currentQuestion && currentQuestion.type === 'checkout' ? 'max-w-2xl' : 'max-w-xl'} slide-container`}>
            {renderStep()}
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default SermorelinForm;
